import { useFormikContext } from 'formik';

import { Callout } from '../../cdl/Callout/Callout';
import { Text } from '../../cdl/Text/Text';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { Box } from '../../common/ui/Box';
import { getDemandCategorySummaries } from '../getDemandCategorySummaries';
import { useDemandDefinitionCalculationContext } from '../hooks/useDemandDefinitionCalculationContext';
import type { DemandCalculation } from '../types/DemandCalculation';
import type { DemandDefinitionState } from '../types/DemandDefinitionState';
import { DemandDefinitionStep } from '../types/DemandDefinitionStep';

import { DemandCategorySummaryTable } from './DemandCategorySummaryTable';
import { DemandReferencePeriodValue } from './DemandReferencePeriodValue';
import { DemandVolume } from './DemandVolume';

interface DemandDefinitionSummaryPanelProps {
    step: DemandDefinitionStep;
    calculation?: DemandCalculation;
    isLoading: boolean;
    isError: boolean;
}

export const DemandDefinitionSummaryPanel = ({
    step,
    calculation,
    isLoading,
    isError,
}: DemandDefinitionSummaryPanelProps) => {
    const { values } = useFormikContext<DemandDefinitionState>();
    const { showHistoricalData, categories, companies } = useDemandDefinitionCalculationContext();
    const selectedCompanyCount = companies.filter((company) => !values.excludedCompanyIds.includes(company.id)).length;
    const total = showHistoricalData && !isError ? calculation?.total : undefined;

    const isHistoricalStep =
        step === DemandDefinitionStep.DEFINE_BASELINE || step === DemandDefinitionStep.REVIEW_BASELINE;
    const title = isHistoricalStep
        ? translate('demandDefinition.historicalDemand')
        : translate('demandDefinition.forecastDemand');
    const volumeField = isHistoricalStep ? 'historicalVolume' : 'adjustedVolume';
    const categorySummaries = getDemandCategorySummaries(total ? calculation : undefined, volumeField);

    return (
        <Box role="region" aria-label={title} display="flex" flexDirection="column" gap={24} aria-busy={isLoading}>
            <Text as="h2" variant="subtitle1">
                {title}
            </Text>
            <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" columnGap={24} rowGap={16}>
                {isHistoricalStep ? (
                    <Box gridColumn="1 / -1" display="flex" flexDirection="column" gap={4}>
                        <Text variant="small" color="foreground.muted" truncated>
                            {translate('demandDefinition.referencePeriod')}
                        </Text>
                        {showHistoricalData ? (
                            <DemandReferencePeriodValue
                                from={values.referencePeriodFrom}
                                to={values.referencePeriodTo}
                            />
                        ) : (
                            <Text variant="subtitle1" truncated>
                                {Characters.EN_DASH}
                            </Text>
                        )}
                    </Box>
                ) : null}
                <Box display="flex" flexDirection="column" minWidth={0} gap={4}>
                    <Text variant="small" color="foreground.muted" truncated>
                        {isHistoricalStep
                            ? translate('demandDefinition.historicalVolume')
                            : translate('demandDefinition.volume')}
                    </Text>
                    <Text variant="subtitle1" truncated>
                        {total ? <DemandVolume volume={total[volumeField]} /> : Characters.EN_DASH}
                    </Text>
                </Box>
                <Box display="flex" flexDirection="column" minWidth={0} gap={4}>
                    <Text variant="small" color="foreground.muted" truncated>
                        {translate('demandDefinition.includedCompanies')}
                    </Text>
                    <Text variant="subtitle1" truncated>
                        {total
                            ? formatNumber({ number: selectedCompanyCount, maximumFractionDigits: 0 })
                            : Characters.EN_DASH}
                    </Text>
                </Box>
                <Box display="flex" flexDirection="column" minWidth={0} gap={4}>
                    <Text variant="small" color="foreground.muted" truncated>
                        {translate('demandDefinition.includedVessels')}
                    </Text>
                    <Text variant="subtitle1" truncated>
                        {total
                            ? formatNumber({ number: total.vesselCount, maximumFractionDigits: 0 })
                            : Characters.EN_DASH}
                    </Text>
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap={12}>
                {total && categorySummaries.length > 0 ? (
                    <DemandCategorySummaryTable
                        variant="inline"
                        categorySummaries={categorySummaries}
                        categories={categories}
                        totalVolume={total[volumeField]}
                    />
                ) : null}
                {total && categorySummaries.length === 0 ? (
                    <Text variant="small" color="foreground.muted">
                        {isHistoricalStep
                            ? translate('demandDefinition.noHistoricalDemand')
                            : translate('demandDefinition.noForecastDemand')}
                    </Text>
                ) : null}
            </Box>
            {isError ? (
                <Callout
                    variant="negative"
                    heading={translate('demandDefinition.calculationFailed')}
                    description={translate('demandDefinition.calculationFailedDescription')}
                />
            ) : null}
        </Box>
    );
};
